export type EstimateStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';

export interface Estimate {
  id: string;
  estimateNumber: string;
  customerId: string;
  jobId?: string;
  templateId?: string;
  issueDate: string;
  validUntil: string;
  status: EstimateStatus;
  subtotal: number;
  taxAmount: number;
  total: number;
  notes?: string;
  convertedToInvoiceId?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  customer?: EstimateCustomer;
  job?: EstimateJob;
  items?: EstimateItem[];
  company?: EstimateCompany;
  template?: EstimateTemplateData;
  convertedToInvoice?: {
    id: string;
    invoiceNumber: string;
  };
}

export interface EstimateCustomer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface EstimateJob {
  id: string;
  jobNumber: string;
  title: string;
  status: string;
}

export interface EstimateItem {
  id: string;
  estimateId: string;
  productId?: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  taxRate?: number;
  taxAmount?: number;
  product?: {
    id: string;
    name: string;
    sku: string;
    itemType: string;
    unitPrice: number;
    isSerialized: boolean;
  };
}

export interface EstimateCompany {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  logo?: string;
}

export interface CreateEstimateData {
  customerId: string;
  jobId?: string;
  templateId: string;
  issueDate?: string;
  validUntil: string;
  notes?: string;
  items: CreateEstimateItemData[];
}

export interface CreateEstimateItemData {
  productId?: string;
  description: string;
  quantity: number;
  rate: number;
  taxRate?: number;
}

export interface UpdateEstimateData {
  customerId?: string;
  jobId?: string;
  issueDate?: string;
  validUntil?: string;
  notes?: string;
  items?: CreateEstimateItemData[];
}

export interface ConvertToInvoiceData {
  dueDate: string;
  notes?: string;
}

export interface EstimateStats {
  totalEstimates: number;
  draftEstimates: number;
  sentEstimates: number;
  acceptedEstimates: number;
  declinedEstimates: number;
  expiredEstimates: number;
  acceptanceRate: number;
  conversionRate: number;
  totalValue: number;
  acceptedValue: number;
}

export interface EstimatesResponse {
  estimates: Estimate[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface EstimateFilters {
  search?: string;
  status?: EstimateStatus;
  customerId?: string;
  jobId?: string;
  page?: number;
  limit?: number;
}

export interface EstimateFormData {
  customerId: string;
  jobId: string;
  templateId: string;
  issueDate: string;
  validUntil: string;
  notes: string;
  items: EstimateItemFormData[];
}

export interface EstimateItemFormData {
  productId?: string;
  description: string;
  quantity: string;
  rate: string;
  taxRate: string;
  amount: number;
  taxAmount: number;
}

export const ESTIMATE_STATUS_LABELS: Record<EstimateStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  EXPIRED: 'Expired'
};

export const ESTIMATE_STATUS_COLORS: Record<EstimateStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  DECLINED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-orange-100 text-orange-800'
};

export interface EstimateTemplateData {
  id: string;
  name: string;
  templateCompanyName: string;
  templateLogo?: string | null;
  logoBase64?: string | null;
  layoutType: 'modern' | 'classic';
  colorScheme: {
    primary: string;
    secondary: string;
    text: string;
    background: string;
  };
  fontFamily: string;
}
